import type {
  ApolloError,
  LazyQueryHookOptions,
  OperationVariables,
  QueryHookOptions,
  QueryResult,
} from '@apollo/client'
import type { QueryTuple } from '@apollo/client/react/types/types'
import { getDisablePolling } from '@backoffice-frontend/environment'
import { useMoiaErrorNotification } from '@backoffice-frontend/patterns'
import { useCallback } from 'react'

type UseQueryFn<TData, TVariables extends OperationVariables> = (
  options: QueryHookOptions<TData, TVariables> &
    ({ variables: TVariables; skip?: boolean } | { skip: boolean }),
) => QueryResult<TData, TVariables>

type UseLazyQueryFn<TData, TVariables extends OperationVariables> = (
  options: LazyQueryHookOptions<TData, TVariables>,
) => QueryTuple<TData, TVariables>

/**
 * Adds error handling to a graphql-code-generator TypeScript React Apollo Hook query
 * @param useQueryFn reference to the actual exported query function
 * @param options Apollo React Hook options as usual
 */
export const useErrorHandlingQuery = <
  TData,
  TVariables extends OperationVariables,
>(
  useQueryFn: UseQueryFn<TData, TVariables>,
  options: QueryHookOptions<TData, TVariables> &
    ({ variables: TVariables; skip?: boolean } | { skip: boolean }),
  getErrorTranslations?: (error: ApolloError) => string,
): QueryResult<TData, TVariables> => {
  const { enqueueMoiaErrorNotification } = useMoiaErrorNotification()
  if (getDisablePolling() && options?.pollInterval) {
    delete options.pollInterval
  }

  const defaultErrorHandler = useCallback(
    (graphqlError: ApolloError) => {
      enqueueMoiaErrorNotification(
        getErrorTranslations
          ? getErrorTranslations(graphqlError)
          : graphqlError,
      )
    },
    [enqueueMoiaErrorNotification, getErrorTranslations],
  )

  const onError = options?.onError ?? defaultErrorHandler

  const result = useQueryFn({
    ...options,
    onError,
    errorPolicy: 'all',
  })

  return result
}

/**
 * Adds error handling to a graphql-code-generator TypeScript React Apollo Hook query
 * @param useLazyQueryFn reference to the actual exported query function
 * @param options Apollo React Hook options as usual
 */
export const useErrorHandlingLazyQuery = <
  TData,
  TVariables extends OperationVariables,
>(
  useLazyQueryFn: UseLazyQueryFn<TData, TVariables>,
  options?: LazyQueryHookOptions<TData, TVariables>,
  getErrorTranslations?: (error: ApolloError) => string,
): QueryTuple<TData, TVariables> => {
  if (getDisablePolling() && options?.pollInterval) {
    delete options.pollInterval
  }
  const { enqueueMoiaErrorNotification } = useMoiaErrorNotification()

  const defaultErrorHandler = useCallback(
    (graphqlError: ApolloError) => {
      enqueueMoiaErrorNotification(
        getErrorTranslations
          ? getErrorTranslations(graphqlError)
          : graphqlError,
      )
    },
    [enqueueMoiaErrorNotification, getErrorTranslations],
  )

  const onError = options?.onError ?? defaultErrorHandler

  return useLazyQueryFn({
    ...options,
    onError,
    errorPolicy: 'all',
  })
}

export enum GraphQlErrorCode {
  // standard
  GRAPHQL_PARSE_FAILED = 'GRAPHQL_PARSE_FAILED',
  GRAPHQL_VALIDATION_FAILED = 'GRAPHQL_VALIDATION_FAILED',
  BAD_USER_INPUT = 'BAD_USER_INPUT',
  UNAUTHENTICATED = 'UNAUTHENTICATED',
  FORBIDDEN = 'FORBIDDEN',
  PERSISTED_QUERY_NOT_FOUND = 'PERSISTED_QUERY_NOT_FOUND',
  PERSISTED_QUERY_NOT_SUPPORTED = 'PERSISTED_QUERY_NOT_SUPPORTED',
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
  SUBREQUEST_HTTP_ERROR = 'SUBREQUEST_HTTP_ERROR',
  // custom
  NOT_FOUND = 'NOT_FOUND',
}

export type HttpStatusWildcard = `${number}xx`
export type ExceptCode = GraphQlErrorCode | number | HttpStatusWildcard

const matchesHttpStatus = (
  status: number | undefined,
  httpCodes: (number | HttpStatusWildcard)[],
): boolean => {
  if (status === undefined) return false
  return httpCodes.some(code => {
    if (typeof code === 'number') return status === code
    // wildcard like '5xx' — match first digit
    const classDigit = Number.parseInt(code[0], 10)
    return Math.floor(status / 100) === classDigit
  })
}

/**
 * Adds error handling to a graphql-code-generator TypeScript React Apollo Hook query, ignoring any of the supplied error codes
 * @param except array of GraphQL error codes and/or HTTP status codes (e.g. 404, '5xx') to be ignored
 * @param useQueryFn reference to the actual exported query function
 * @param options Apollo React Hook options as usual
 */
export const useErrorHandlingQueryExcept = <
  TData,
  TVariables extends OperationVariables,
>(
  except: ExceptCode[],
  useQueryFn: UseQueryFn<TData, TVariables>,
  options: QueryHookOptions<TData, TVariables> &
    ({ variables: TVariables; skip?: boolean } | { skip: boolean }),
  getErrorTranslations?: (error: ApolloError) => string,
): QueryResult<TData, TVariables> => {
  if (getDisablePolling() && options?.pollInterval) {
    delete options.pollInterval
  }
  const { enqueueMoiaErrorNotification } = useMoiaErrorNotification()

  const defaultErrorHandler = useCallback<(error: ApolloError) => void>(
    graphqlError => {
      const graphqlCodes = except.filter(
        (code): code is GraphQlErrorCode =>
          typeof code === 'string' && code in GraphQlErrorCode,
      )
      const httpCodes = except.filter(
        (code): code is number | HttpStatusWildcard =>
          typeof code === 'number' ||
          (typeof code === 'string' && /^\d+xx$/.test(code)),
      )

      const skipByGraphQlCode = graphqlError.graphQLErrors.find(
        err =>
          // @ts-expect-error apollo does no longer ship nested types for errors
          graphqlCodes.includes(err?.extensions?.code) ||
          // @ts-expect-error apollo does no longer ship nested types for errors
          graphqlCodes.includes(err?.extensions?.error?.extensions?.code),
      )

      const skipByHttpStatus =
        httpCodes.length > 0 &&
        (graphqlError.graphQLErrors.some(
          // @ts-expect-error apollo does no longer ship nested types for errors
          err => matchesHttpStatus(err?.extensions?.http?.status, httpCodes),
        ) ||
          matchesHttpStatus(
            (graphqlError.networkError as { statusCode?: number })?.statusCode,
            httpCodes,
          ))

      if (graphqlError && !skipByGraphQlCode && !skipByHttpStatus) {
        enqueueMoiaErrorNotification(
          getErrorTranslations
            ? getErrorTranslations(graphqlError)
            : graphqlError,
        )
      }
    },
    [enqueueMoiaErrorNotification, except, getErrorTranslations],
  )

  const onError = options?.onError ?? defaultErrorHandler

  const result = useQueryFn({
    ...options,
    onError,
    errorPolicy: 'all',
  })

  return result
}

const POLLING_EXCEPT_CODES: ExceptCode[] = ['5xx']

/**
 * Adds error handling to a polling query, silencing 5xx errors that are not actionable during background polling.
 * Requires pollInterval to be set.
 * @param useQueryFn reference to the actual exported query function
 * @param options Apollo React Hook options — pollInterval is required
 */
export const useErrorHandlingPollingQuery = <
  TData,
  TVariables extends OperationVariables,
>(
  useQueryFn: UseQueryFn<TData, TVariables>,
  // onError is deprecated in Apollo Client — omit it so new consumers don't adopt it.
  // TODO: remove the Omit once we upgrade to an Apollo version that drops onError from QueryHookOptions.
  options: Omit<QueryHookOptions<TData, TVariables>, 'onError'> &
    (
      | { variables: TVariables; skip?: boolean; pollInterval: number }
      | { skip: true }
    ),
  getErrorTranslations?: (error: ApolloError) => string,
): QueryResult<TData, TVariables> => {
  return useErrorHandlingQueryExcept(
    POLLING_EXCEPT_CODES,
    useQueryFn,
    options,
    getErrorTranslations,
  )
}
